import axios from 'axios';
import { API_AI } from '../../../api/api.base';
import type {
	RetryTomMessageRequest,
	SendTomMessageRequest,
	TomApiEmptyResponse,
	TomApiErrorInfo,
	TomApiResponse,
	TomConversation,
	TomConversationMessagesPage,
	TomConversationPage,
	TomMessageSourceFileLink,
	TomStreamEvent,
} from '../types';
import { TomApiError } from './tomApiError';
import { consumeTomStream, readTomStreamText } from './tomStreamParser';

const CONVERSATIONS_PAGE_SIZE = 50;
const MESSAGES_PAGE_SIZE = 100;
const TOM_AI_BASE_PATH = '/api/app/tom-ai';

const isRecord = (value: unknown): value is Record<string, unknown> =>
	typeof value === 'object' && value !== null;

const isErrorInfo = (value: unknown): value is TomApiErrorInfo =>
	isRecord(value) &&
	typeof value.key === 'string' &&
	typeof value.message === 'string';

const getFirstError = (value: unknown): TomApiErrorInfo | undefined => {
	if (!isRecord(value) || !Array.isArray(value.errors)) {
		return undefined;
	}

	return value.errors.find(isErrorInfo);
};

const unwrap = <T>(response: TomApiResponse<T>): T => {
	if (!response.success) {
		const error = response.errors?.[0];
		throw new TomApiError(
			error?.message || 'Tom AI request failed.',
			response.status,
			error?.key,
		);
	}

	return response.value;
};

const ensureSuccess = (response: TomApiEmptyResponse) => {
	if (!response.success) {
		const error = response.errors?.[0];
		throw new TomApiError(
			error?.message || 'Tom AI request failed.',
			response.status,
			error?.key,
		);
	}
};

const toTomApiError = async (error: unknown) => {
	if (error instanceof TomApiError) {
		return error;
	}

	if (!axios.isAxiosError(error)) {
		return new TomApiError(
			error instanceof Error ? error.message : 'Tom AI request failed.',
		);
	}

	let payload: unknown = error.response?.data;
	if (payload instanceof ReadableStream) {
		try {
			const body = await readTomStreamText(payload);
			payload = body ? JSON.parse(body) : undefined;
		} catch {
			payload = undefined;
		}
	}

	if (typeof payload === 'string') {
		const payloadText = payload;
		try {
			payload = JSON.parse(payloadText);
		} catch {
			return new TomApiError(
				payloadText || error.message,
				error.response?.status,
			);
		}
	}

	const apiError = getFirstError(payload);
	return new TomApiError(
		apiError?.message || error.message || 'Tom AI request failed.',
		error.response?.status,
		apiError?.key,
	);
};

const streamRequest = async (
	url: string,
	body: SendTomMessageRequest | RetryTomMessageRequest,
	signal: AbortSignal,
	onEvent: (event: TomStreamEvent) => void,
) => {
	try {
		const stream = await API_AI.postStream(url, body, { signal });
		await consumeTomStream(stream, onEvent);
	} catch (error) {
		throw await toTomApiError(error);
	}
};

export const tomApi = {
	async getConversations(cursor: string | null) {
		const response = (await API_AI.get(
			`${TOM_AI_BASE_PATH}/conversations`,
			{
				limit: CONVERSATIONS_PAGE_SIZE,
				cursor: cursor || undefined,
			},
		)) as TomApiResponse<TomConversationPage>;

		return unwrap(response);
	},

	async createConversation(title: string | null = null) {
		const response = (await API_AI.post(
			`${TOM_AI_BASE_PATH}/conversations`,
			{
				title,
			},
		)) as unknown as TomApiResponse<TomConversation>;

		return unwrap(response);
	},

	async archiveConversation(conversationId: number) {
		const response = (await API_AI.delete(
			`${TOM_AI_BASE_PATH}/conversations/${conversationId}`,
		)) as TomApiEmptyResponse;

		ensureSuccess(response);
	},

	async getMessages(conversationId: number, cursor: string | null) {
		const response = (await API_AI.get(
			`${TOM_AI_BASE_PATH}/conversations/${conversationId}/messages`,
			{
				limit: MESSAGES_PAGE_SIZE,
				cursor: cursor || undefined,
			},
		)) as TomApiResponse<TomConversationMessagesPage>;

		return unwrap(response);
	},

	async getSourceFile(
		conversationId: number,
		messageId: number,
		knowledgePublicId: string,
		assetId: number,
	) {
		const response = (await API_AI.get(
			`${TOM_AI_BASE_PATH}/conversations/${conversationId}/messages/${messageId}/sources/${knowledgePublicId}/files/${assetId}`,
			{},
		)) as TomApiResponse<TomMessageSourceFileLink>;

		return unwrap(response);
	},

	streamMessage(
		conversationId: number,
		request: SendTomMessageRequest,
		signal: AbortSignal,
		onEvent: (event: TomStreamEvent) => void,
	) {
		return streamRequest(
			`${TOM_AI_BASE_PATH}/conversations/${conversationId}/messages/stream`,
			request,
			signal,
			onEvent,
		);
	},

	retryMessage(
		conversationId: number,
		assistantMessageId: number,
		request: RetryTomMessageRequest,
		signal: AbortSignal,
		onEvent: (event: TomStreamEvent) => void,
	) {
		return streamRequest(
			`${TOM_AI_BASE_PATH}/conversations/${conversationId}/messages/${assistantMessageId}/retry/stream`,
			request,
			signal,
			onEvent,
		);
	},
};

export { TomApiError } from './tomApiError';
